import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { 
  Palette, 
  Code, 
  Database, 
  Smartphone,
  Cloud,
  Shield,
  Zap,
  ArrowRight,
  CheckCircle,
  Star
} from "lucide-react";
import { 
  Container, 
  Section, 
  Grid, 
  Card, 
  PrimaryButton, 
  Badge
} from "../styles/GlobalStyles";

// Enhanced Features Section
const FeaturesSection = styled(Section)`
  background: var(--gray-0);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -25%;
    width: 150%;
    height: 50%;
    background: radial-gradient(ellipse at center, rgba(59, 130, 246, 0.03) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    margin-bottom: var(--space-12);
  }
  
  @media (max-width: 480px) {
    margin-bottom: var(--space-8);
  }
  
  .badge {
    margin-bottom: var(--space-4);
    
    @media (max-width: 480px) {
      margin-bottom: var(--space-3);
    }
  }
  
  h2 {
    font-size: var(--text-5xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
    font-weight: 800;
    
    @media (max-width: 1024px) {
      font-size: var(--text-4xl);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
      margin-bottom: var(--space-4);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-2xl);
      margin-bottom: var(--space-3);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    color: var(--gray-600);
    max-width: 700px;
    margin: 0 auto;
    line-height: var(--leading-relaxed);
    
    @media (max-width: 768px) {
      font-size: var(--text-lg);
      max-width: 100%;
      padding: 0 var(--space-4);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-base);
      padding: 0 var(--space-2);
    }
  }
`;

const FeatureCard = styled(Card)`
  padding: var(--space-10);
  text-align: center;
  border: 2px solid transparent;
  background: var(--gray-0);
  transition: all var(--transition-normal);
  cursor: pointer;
  position: relative;
  
  @media (max-width: 768px) {
    padding: var(--space-6);
  }
  
  @media (max-width: 480px) {
    padding: var(--space-4);
  }
  
  &:hover {
    border-color: var(--primary-200);
    transform: translateY(-12px) scale(1.02);
    box-shadow: var(--shadow-2xl);
    
    @media (max-width: 768px) {
      transform: translateY(-8px) scale(1.01);
    }
    
    @media (max-width: 480px) {
      transform: translateY(-4px) scale(1.005);
    }
    
    .icon {
      transform: scale(1.1) rotate(5deg);
      background: var(--gradient-primary);
      color: var(--gray-0);
      
      @media (max-width: 768px) {
        transform: scale(1.05) rotate(3deg);
      }
    }
    
    .cta-button {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .icon {
    width: 80px;
    height: 80px;
    margin: 0 auto var(--space-6);
    background: var(--primary-50);
    border-radius: var(--radius-3xl);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-600);
    transition: all var(--transition-normal);
  }
  
  h3 {
    font-size: var(--text-2xl);
    color: var(--gray-900);
    margin-bottom: var(--space-4);
    font-weight: 700;
  }
  
  p {
    color: var(--gray-600);
    line-height: var(--leading-relaxed);
    margin-bottom: var(--space-6);
    font-size: var(--text-base);
  }
  
  .features-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-bottom: var(--space-8);
    text-align: left;
  }
  
  .feature-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    color: var(--gray-700);
    
    .check {
      color: var(--secondary-600);
      flex-shrink: 0;
    }
  }
  
  .cta-button {
    opacity: 0;
    transform: translateY(10px);
    transition: all var(--transition-normal);
    width: 100%;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -10px;
  right: var(--space-6);
  background: var(--gradient-accent);
  color: var(--gray-0);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: var(--space-1);
  box-shadow: var(--shadow-lg);
`;

const ProcessSection = styled.div`
  margin-top: var(--space-20);
  text-align: center;
  
  h3 {
    font-size: var(--text-3xl);
    color: var(--gray-900);
    margin-bottom: var(--space-12);
    font-weight: 700;
  }
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-8);
  margin-top: var(--space-12);
`;

const ProcessStep = styled.div`
  position: relative;
  
  .step-number {
    width: 60px;
    height: 60px;
    background: var(--gradient-primary);
    color: var(--gray-0);
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xl);
    font-weight: 800;
    margin: 0 auto var(--space-6);
    position: relative;
    z-index: 2;
  }
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 30px;
    left: calc(50% + 30px);
    width: calc(100% - 60px);
    height: 2px;
    background: var(--primary-200);
    z-index: 1;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  h4 {
    font-size: var(--text-lg);
    color: var(--gray-900);
    margin-bottom: var(--space-3);
    font-weight: 600;
  }
  
  p {
    color: var(--gray-600);
    font-size: var(--text-sm);
    line-height: var(--leading-relaxed);
    margin: 0;
  }
`;

const features = [
  {
    title: "AI-Powered Restaurant Management",
    description: "Next-gen restaurant solutions with AI analytics, predictive ordering, and smart inventory management - demonstrated at WillovateResto.com",
    icon: <Database size={40} />,
    features: [
      "AI-driven inventory predictions",
      "Smart menu optimization",
      "Automated order processing",
      "Predictive analytics & insights",
      "Live AI demo: willovateresto.com"
    ],
    popular: true,
    link: "https://willovateresto.com"
  },
  {
    title: "AI-Enhanced Web Applications",
    description: "Build intelligent web apps with AI integration using React, .NET Core, Python, and cutting-edge AI technologies.",
    icon: <Code size={40} />,
    features: [
      "GenAI & LLM integration",
      "AI-powered chatbots",
      "Smart data processing",
      "Machine learning models",
      "Cross-platform mobile apps"
    ],
    link: "/web-development"
  },
  {
    title: "AI-Driven UI/UX Design",
    description: "Create intelligent user interfaces with AI assistance, personalization, and adaptive design systems.",
    icon: <Palette size={40} />,
    features: [
      "AI-assisted design generation",
      "Personalized user experiences",
      "Intelligent A/B testing",
      "Voice & gesture interfaces",
      "Accessibility optimization"
    ],
    link: "/ui-ux-design"
  },
  {
    title: "Smart Database & Analytics",
    description: "Intelligent data solutions with AI-powered analytics, automated insights, and predictive capabilities.",
    icon: <Smartphone size={40} />,
    features: [
      "AI-powered query optimization",
      "Predictive data modeling",
      "Automated insight generation",
      "Real-time analytics dashboards",
      "Smart data visualization"
    ],
    link: "/database-management"
  },
  {
    title: "AI Startup Acceleration",
    description: "Launch your AI-powered startup with our comprehensive AI development and strategy support.",
    icon: <Cloud size={40} />,
    features: [
      "AI MVP development",
      "LLM integration strategies",
      "AI product roadmapping",
      "Scalable AI architecture",
      "AI ethics & compliance"
    ],
    link: "/startup-solutions"
  },
  {
    title: "AI System Maintenance",
    description: "Keep your AI applications running optimally with continuous monitoring, model updates, and performance optimization.",
    icon: <Shield size={40} />,
    features: [
      "AI model monitoring & updates",
      "Performance optimization",
      "Security & bias auditing",
      "Feature enhancement with AI",
      "24/7 intelligent support"
    ],
    link: "/maintenance-support"
  }
];

const processSteps = [
  {
    number: "01",
    title: "Discovery & Planning",
    description: "We analyze your requirements and create a comprehensive project roadmap."
  },
  {
    number: "02", 
    title: "Design & Prototype",
    description: "Our team creates wireframes and prototypes to visualize your solution."
  },
  {
    number: "03",
    title: "Development & Testing", 
    description: "We build your solution using agile methodology with continuous testing."
  },
  {
    number: "04",
    title: "Launch & Support",
    description: "We deploy your solution and provide ongoing maintenance and support."
  }
];

const Features = () => {
  const navigate = useNavigate();
  
  const handleFeatureClick = (link) => {
    navigate('/contact');
  };

  return (
    <FeaturesSection>
      <Container>
        <SectionHeader>
          <Badge className="badge primary">
            <Zap size={12} />
            AI-Powered Solutions
          </Badge>
          <h2>Next-Generation AI Development Services</h2>
          <p className="subtitle">
            We build intelligent applications powered by AI, machine learning, and cutting-edge technologies. 
            From AI-enhanced web apps to intelligent mobile solutions - we create software that thinks and adapts.
          </p>
        </SectionHeader>

        <Grid className="cols-3">
          {features.map((feature, index) => (
            <FeatureCard 
              key={index}
              onClick={() => handleFeatureClick(feature.link)}
            >
              {feature.popular && (
                <PopularBadge>
                  <Star size={12} />
                  Popular
                </PopularBadge>
              )}
              
              <div className="icon">
                {feature.icon}
              </div>
              
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
              
              <div className="features-list">
                {feature.features.map((item, idx) => (
                  <div key={idx} className="feature-item">
                    <CheckCircle size={16} className="check" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              
              <PrimaryButton className="cta-button" onClick={() => handleFeatureClick(feature.link)}>
                Learn More
                <ArrowRight size={16} />
              </PrimaryButton>
            </FeatureCard>
          ))}
        </Grid>

        <ProcessSection>
          <h3>Our Proven Development Process</h3>
          <ProcessGrid>
            {processSteps.map((step, index) => (
              <ProcessStep key={index}>
                <div className="step-number">{step.number}</div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </ProcessStep>
            ))}
          </ProcessGrid>
        </ProcessSection>
      </Container>
    </FeaturesSection>
  );
};

export default Features;
